import { dehydrate, hydrate, type QueryClient } from '@tanstack/react-query';

/**
 * Warm cache for the Budgets page.
 *
 * The pacing snapshot, the account scope and the autonomous plan are slow to
 * compute (Planner plus several Google reads) and the QueryClient forgets them
 * on a reload or after ten idle minutes. This persists exactly those queries to
 * localStorage so a revisit paints the last known numbers at once and refetches
 * in the background. Stale-while-revalidate, never stale-instead-of-revalidate:
 * staleTime on the queries is unchanged, so anything older than a minute
 * refetches on mount; the page just does not go blank while it does.
 *
 * Only successful queries whose key starts with one of PERSISTED_QUERY_PREFIXES
 * are written. Entries older than MAX_AGE_MS or from another PERSIST_VERSION
 * are dropped on load. Storage failures are swallowed: the page must work with
 * no storage at all.
 *
 * Lifecycle: nothing is read or written until a signed-in user is known
 * (`activateBudgetPacingPersistence(userId)`); the storage key is namespaced by
 * that user, so another account on the same browser cannot hydrate from it;
 * `clearBudgetPacingPersistence()` on sign-out removes the entry and the
 * matching in-memory queries, so no spend figures outlive the session.
 */

export const PERSIST_KEY_PREFIX = 'agileco.google-ads.budget-pacing.cache.v1';

export function persistKeyFor(userId: string): string {
  return `${PERSIST_KEY_PREFIX}:${userId}`;
}
export const PERSIST_VERSION = 1;
export const MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** Persisted queries must outlive a navigation, or the next save drops them. */
export const PERSISTED_GC_TIME_MS = MAX_AGE_MS;
export const SAVE_THROTTLE_MS = 1_000;
const MAX_BYTES = 1_500_000;

// Read-only figures only. The set-aside approval queue is deliberately not
// here: a day-old item behind Approve/Reject buttons must never be painted
// from a blob (Viktor, #2562).
export const PERSISTED_QUERY_PREFIXES = [
  'google-ads-budget-pacing',
  'google-ads-pacing-account-scope',
  'google-ads-autonomous-pacing',
  'google-ads-pacing-ceiling',
] as const;

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface PersistedEnvelope {
  version: number;
  savedAt: number;
  state: ReturnType<typeof dehydrate>;
}

export function isPersistedKey(queryKey: readonly unknown[]): boolean {
  const head = queryKey[0];
  return typeof head === 'string' && (PERSISTED_QUERY_PREFIXES as readonly string[]).includes(head);
}

export function shouldPersistQuery(query: { queryKey: readonly unknown[]; state: { status: string } }): boolean {
  return query.state.status === 'success' && isPersistedKey(query.queryKey);
}

/** Read and validate the envelope; null when absent, expired, foreign or corrupt. */
export function loadPersistedState(storage: StorageLike, now: number, key: string): PersistedEnvelope['state'] | null {
  let raw: string | null = null;
  try {
    raw = storage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const env = JSON.parse(raw) as Partial<PersistedEnvelope>;
    if (env.version !== PERSIST_VERSION || typeof env.savedAt !== 'number' || !env.state) return null;
    if (now - env.savedAt > MAX_AGE_MS) {
      try {
        storage.removeItem(key);
      } catch {
        /* ignore */
      }
      return null;
    }
    const queries = (env.state.queries ?? []).filter(
      (q) => isPersistedKey(q.queryKey as readonly unknown[]) && q.state?.status === 'success',
    );
    return { ...env.state, queries, mutations: [] };
  } catch {
    return null;
  }
}

export function savePersistedState(client: QueryClient, storage: StorageLike, now: number, key: string): boolean {
  try {
    const state = dehydrate(client, { shouldDehydrateQuery: shouldPersistQuery, shouldDehydrateMutation: () => false });
    if (state.queries.length === 0) {
      storage.removeItem(key);
      return false;
    }
    const raw = JSON.stringify({ version: PERSIST_VERSION, savedAt: now, state } satisfies PersistedEnvelope);
    if (raw.length > MAX_BYTES) return false;
    storage.setItem(key, raw);
    return true;
  } catch {
    return false;
  }
}

/** "3 min ago", "2 h ago", or null when fresh (under a minute). */
export function cacheAgeLabel(dataUpdatedAt: number | undefined, now: number = Date.now()): string | null {
  if (!dataUpdatedAt) return null;
  const ageMs = now - dataUpdatedAt;
  if (ageMs < 60_000) return null;
  const minutes = Math.round(ageMs / 60_000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return `${hours} h ago`;
}

/**
 * Copy for the vintage line above the money row. Driven by staleness and
 * error, never by "a fetch is in flight": if the background refresh fails,
 * yesterday's figures must still carry their age.
 */
export function warmCacheNote(ageLabel: string | null, opts: { isFetching: boolean; isError: boolean }): string | null {
  if (!ageLabel) return null;
  if (opts.isError) return `Showing figures from ${ageLabel}; the refresh failed. Press Refresh to try again.`;
  if (opts.isFetching) return `Showing figures from ${ageLabel}, refreshing in the background.`;
  return `Showing figures from ${ageLabel}.`;
}

interface PersistController {
  client: QueryClient;
  storage: StorageLike;
  now: () => number;
  activeKey: string | null;
  hydratedFor: string | null;
  timer: ReturnType<typeof setTimeout> | null;
  unsubscribe: () => void;
}

let controller: PersistController | null = null;

function scheduleSave(): void {
  const c = controller;
  if (!c || !c.activeKey || c.timer) return;
  c.timer = setTimeout(() => {
    if (!controller) return;
    controller.timer = null;
    if (controller.activeKey) savePersistedState(controller.client, controller.storage, controller.now(), controller.activeKey);
  }, SAVE_THROTTLE_MS);
}

/**
 * Called once a signed-in user is known. Hydrates from that user's entry
 * (once per user) and enables saves under that key.
 */
export function activateBudgetPacingPersistence(userId: string | null | undefined): void {
  const c = controller;
  if (!c) return;
  if (!userId) {
    c.activeKey = null;
    return;
  }
  const key = persistKeyFor(userId);
  if (c.hydratedFor && c.hydratedFor !== userId) {
    // Account switch without a sign-out (SIGNED_IN over a live session): the
    // previous user's figures must leave memory before the next save, or
    // they would be written into this user's entry. Their own storage entry
    // stays for when they come back.
    if (c.timer) {
      clearTimeout(c.timer);
      c.timer = null;
    }
    try {
      c.client.removeQueries({ predicate: (q) => isPersistedKey(q.queryKey) });
    } catch {
      /* ignore */
    }
  }
  c.activeKey = key;
  if (c.hydratedFor === userId) return;
  c.hydratedFor = userId;
  const restored = loadPersistedState(c.storage, c.now(), key);
  if (restored) {
    try {
      hydrate(c.client, restored, { defaultOptions: { queries: { gcTime: PERSISTED_GC_TIME_MS } } });
    } catch {
      /* a bad envelope must never block the app */
    }
  }
}

/** Sign-out: no spend figures outlive the session, in storage or in memory. */
export function clearBudgetPacingPersistence(): void {
  const c = controller;
  if (!c) return;
  if (c.timer) {
    clearTimeout(c.timer);
    c.timer = null;
  }
  if (c.activeKey) {
    try {
      c.storage.removeItem(c.activeKey);
    } catch {
      /* ignore */
    }
  }
  c.activeKey = null;
  c.hydratedFor = null;
  try {
    c.client.removeQueries({ predicate: (q) => isPersistedKey(q.queryKey) });
  } catch {
    /* ignore */
  }
}

/**
 * Install the cache subscription. Nothing is read or written until
 * `activateBudgetPacingPersistence(userId)`. Returns a teardown. Safe to call
 * with no storage.
 */
export function setupBudgetPacingPersistence(
  client: QueryClient,
  storage: StorageLike | null = typeof window !== 'undefined' ? window.localStorage : null,
  now: () => number = Date.now,
): () => void {
  if (!storage) return () => {};
  if (controller) controller.unsubscribe();
  const unsubscribe = client.getQueryCache().subscribe((event) => {
    const key = (event as { query?: { queryKey?: readonly unknown[] } }).query?.queryKey;
    if (!key || !isPersistedKey(key)) return;
    scheduleSave();
  });
  controller = { client, storage, now, activeKey: null, hydratedFor: null, timer: null, unsubscribe };
  return () => {
    if (controller?.timer) clearTimeout(controller.timer);
    unsubscribe();
    controller = null;
  };
}
